/**
 * Service Angular pour la gestion de la géolocalisation
 * @module LocationService
 */

import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { LogService } from './log.service';
import { Observable, throwError, of, timer } from 'rxjs';
import {
  map,
  catchError,
  retry,
  tap,
  shareReplay,
  debounceTime,
  switchMap,
} from 'rxjs/operators';
import {
  AddressSuggestion,
  ValidatedAddress,
  EnrichedLocationData,
  UserLocationProfile,
  SearchAddressResponse,
  ValidateAddressResponse,
  EnrichLocationResponse,
  LocationProfileResponse,
  Coordinates,
  RouteResult,
  EventWeatherForecast,
  WeatherSlotsResponse,
  NearbyEventsResponse,
} from '../models/location.models';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  //  private apiUrl = 'http://analyticai.com:3000/api/location';
  private apiUrl = '/api/location';
  private log = inject(LogService);

  // États de chargement
  private readonly _searching = signal<boolean>(false);
  readonly searching = this._searching.asReadonly();

  private readonly _validating = signal<boolean>(false);
  readonly validating = this._validating.asReadonly();

  private readonly _enriching = signal<boolean>(false);
  readonly enriching = this._enriching.asReadonly();

  // État de progression de l'enrichissement
  private readonly _enrichmentProgress = signal<number>(0);
  readonly enrichmentProgress = this._enrichmentProgress.asReadonly();

  // Cache du profil utilisateur
  private readonly _locationProfile = signal<UserLocationProfile | null>(null);
  readonly locationProfile = this._locationProfile.asReadonly();

  constructor(private readonly http: HttpClient) {}

  // =====================================================
  // RECHERCHE D'ADRESSES
  // =====================================================

  /**
   * Recherche d'adresses avec autocomplete
   * Inclut un debounce automatique
   */
  searchAddress(
    query: string,
    limit: number = 5,
  ): Observable<AddressSuggestion[]> {
    if (!query || query.length < 3) {
      return of([]);
    }

    this._searching.set(true);

    return this.http
      .post<SearchAddressResponse>(`${this.apiUrl}/search`, { query, limit })
      .pipe(
        map((response) => response.suggestions),
        tap(() => this._searching.set(false)),
        catchError((error) => {
          this._searching.set(false);
          return this.handleError(
            error,
            "Erreur lors de la recherche d'adresse",
          );
        }),
      );
  }

  /**
   * Recherche d'adresses avec debounce (pour input utilisateur)
   */
  searchAddressDebounced(
    query$: Observable<string>,
    limit: number = 5,
  ): Observable<AddressSuggestion[]> {
    return query$.pipe(
      debounceTime(300), // Attendre 300ms après la dernière frappe
      switchMap((query) => this.searchAddress(query, limit)),
    );
  }

  // =====================================================
  // VALIDATION D'ADRESSE
  // =====================================================

  /**
   * Valide une adresse et retourne ses coordonnées
   */
  validateAddress(
    address: string,
    useFallback: boolean = true,
  ): Observable<ValidatedAddress | null> {
    this._validating.set(true);

    return this.http
      .post<ValidateAddressResponse>(`${this.apiUrl}/validate`, {
        address,
        normalize: true,
        useFallback,
      })
      .pipe(
        map((response) =>
          response.validated && response.address && response.address.valid
            ? response.address
            : null,
        ),
        tap((addr) => {
          if (addr) {
            this.log.debug('Adresse validée', {
              formatted: addr.formatted,
              source: addr.source,
              confidence: addr.confidence,
            });
          }
        }),
        tap(() => this._validating.set(false)),
        retry(2),
        catchError((error) => {
          this._validating.set(false);
          return this.handleError(
            error,
            "Erreur lors de la validation de l'adresse",
          );
        }),
      );
  }

  // =====================================================
  // ENRICHISSEMENT
  // =====================================================

  /**
   * Enrichit une localisation (mode synchrone)
   * @param radius - Rayon en mètres (optionnel, le backend détermine automatiquement si non fourni)
   * @param address - Adresse sélectionnée par l'utilisateur (optionnel, évite le reverse geocode)
   */
  enrichLocation(
    lat: number,
    lng: number,
    radius?: number,
    userId?: string,
    address?: string,
  ): Observable<EnrichedLocationData> {
    this._enriching.set(true);
    this._enrichmentProgress.set(0);

    // Ne pas envoyer radius si undefined (le backend calculera selon activityType)
    const body: Record<string, unknown> = {
      lat,
      lng,
      async: false,
    };
    if (radius !== undefined) body['radius'] = radius;
    if (userId !== undefined) body['userId'] = userId;
    if (address !== undefined) body['address'] = address;

    return this.http
      .post<EnrichLocationResponse>(`${this.apiUrl}/enrich`, body)
      .pipe(
        tap(() => this._enrichmentProgress.set(50)),
        map((response) => {
          if (!response.data) {
            throw new Error('Enrichissement incomplet');
          }
          this._enrichmentProgress.set(100);
          return response.data;
        }),
        tap((data) => {
          this._enriching.set(false);
          this.log.debug('Enrichissement terminé');
        }),
        catchError((error) => {
          this._enriching.set(false);
          this._enrichmentProgress.set(0);
          return this.handleError(error, "Erreur lors de l'enrichissement");
        }),
      );
  }

  /**
   * Enrichit une localisation (mode asynchrone avec job)
   * @param radius - Rayon en mètres (optionnel, le backend détermine automatiquement si non fourni)
   */
  enrichLocationAsync(
    lat: number,
    lng: number,
    radius?: number,
    userId?: string,
  ): Observable<string> {
    // Ne pas envoyer radius si undefined (le backend calculera selon activityType)
    const body: Record<string, unknown> = {
      lat,
      lng,
      async: true,
    };
    if (radius !== undefined) body['radius'] = radius;
    if (userId !== undefined) body['userId'] = userId;

    return this.http
      .post<EnrichLocationResponse>(`${this.apiUrl}/enrich`, body)
      .pipe(
        map((response: any) => {
          if (!response.jobId) {
            throw new Error('Job non créé');
          }
          return response.jobId;
        }),
        catchError((error) =>
          this.handleError(error, 'Erreur lors de la création du job'),
        ),
      );
  }

  // =====================================================
  // PROFIL UTILISATEUR
  // =====================================================

  /**
   * Récupère le profil de localisation d'un utilisateur
   */
  getLocationProfile(
    userId: string,
    forceRefresh: boolean = false,
  ): Observable<LocationProfileResponse> {
    // Si en cache et pas de refresh forcé, retourner le cache
    if (!forceRefresh && this._locationProfile()) {
      return of(this.mapToProfileResponse(userId, this._locationProfile()!));
    }

    return this.http
      .get<LocationProfileResponse>(`${this.apiUrl}/profile/${userId}`)
      .pipe(
        tap((profile) => {
          if (profile.hasLocation && profile.enrichedData) {
            const userProfile: UserLocationProfile = {
              address: profile.address!,
              city: profile.city!,
              postalCode: profile.postalCode!,
              country: profile.country!,
              coordinates: profile.coordinates!,
              actionRadius: profile.actionRadius,
              enrichedData: profile.enrichedData,
              enrichedAt: new Date(profile.enrichedAt!),
              needsUpdate: profile.needsUpdate,
              enrichmentStatus: profile.enrichmentStatus,
            };
            this._locationProfile.set(userProfile);
          }
        }),
        shareReplay(1), // Partager le résultat avec plusieurs subscribers
        catchError((error) =>
          this.handleError(error, 'Erreur lors de la récupération du profil'),
        ),
      );
  }

  /**
   * Met à jour le profil de localisation
   */
  updateLocationProfile(
    userId: string,
    updates: {
      address?: string;
      city?: string;
      postalCode?: string;
      country?: string;
      coordinates?: Coordinates;
      actionRadius?: number;
    },
  ): Observable<LocationProfileResponse> {
    return this.http
      .put<LocationProfileResponse>(`${this.apiUrl}/profile/${userId}`, updates)
      .pipe(
        tap((profile) => {
          // Mettre à jour le cache
          if (profile.hasLocation && profile.enrichedData) {
            const userProfile: UserLocationProfile = {
              address: profile.address!,
              city: profile.city!,
              postalCode: profile.postalCode!,
              country: profile.country!,
              coordinates: profile.coordinates!,
              actionRadius: profile.actionRadius,
              enrichedData: profile.enrichedData,
              enrichedAt: new Date(profile.enrichedAt!),
              needsUpdate: profile.needsUpdate,
              enrichmentStatus: profile.enrichmentStatus,
            };
            this._locationProfile.set(userProfile);
          }
        }),
        catchError((error) =>
          this.handleError(error, 'Erreur lors de la mise à jour du profil'),
        ),
      );
  }

  /**
   * Rafraîchit les données enrichies d'un utilisateur
   */
  refreshLocationData(
    userId: string,
  ): Observable<{ success: boolean; data: EnrichedLocationData }> {
    this._enriching.set(true);

    return this.http
      .post<{
        success: boolean;
        data: EnrichedLocationData;
      }>(`${this.apiUrl}/profile/${userId}/refresh`, {})
      .pipe(
        tap((result) => {
          this._enriching.set(false);
          if (result.success) {
            // Mettre à jour le cache
            const current = this._locationProfile();
            if (current) {
              this._locationProfile.set({
                ...current,
                enrichedData: result.data,
                enrichedAt: new Date(),
                needsUpdate: false,
              });
            }
          }
        }),
        catchError((error) => {
          this._enriching.set(false);
          return this.handleError(error, 'Erreur lors du rafraîchissement');
        }),
      );
  }

  /**
   * Supprime le profil de localisation
   */
  deleteLocationProfile(userId: string): Observable<boolean> {
    return this.http
      .delete<{ success: boolean }>(`${this.apiUrl}/profile/${userId}`)
      .pipe(
        tap(() => {
          this._locationProfile.set(null);
        }),
        map((result) => result.success),
        catchError((error) =>
          this.handleError(error, 'Erreur lors de la suppression'),
        ),
      );
  }

  // =====================================================
  // GÉOLOCALISATION NAVIGATEUR
  // =====================================================

  /**
   * Obtient la position actuelle de l'utilisateur
   */
  getCurrentPosition(): Observable<Coordinates> {
    return new Observable((observer) => {
      if (!navigator.geolocation) {
        observer.error(
          new Error(
            "La géolocalisation n'est pas supportée par votre navigateur",
          ),
        );
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          observer.next({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          observer.complete();
        },
        (error) => {
          let message = 'Erreur de géolocalisation';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'Permission de géolocalisation refusée';
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Position non disponible';
              break;
            case error.TIMEOUT:
              message = 'Délai de géolocalisation expiré';
              break;
          }
          observer.error(new Error(message));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    });
  }

  reverseGeocode(
    lat: number,
    lng: number,
  ): Observable<ValidatedAddress | null> {
    return this.http
      .post<ValidateAddressResponse>(`${this.apiUrl}/reverse`, {
        lat,
        lng,
        normalize: true,
        useFallback: true,
      })
      .pipe(
        map((response) =>
          response.validated && response.address ? response.address : null,
        ),
        catchError(() => of(null)),
      );
  }

  /**
   * Obtient l'adresse depuis la position actuelle
   */
  /**
   * Obtient l'adresse depuis la position actuelle
   */
  getAddressFromCurrentPosition(): Observable<ValidatedAddress | null> {
    return this.getCurrentPosition().pipe(
      switchMap((coords) => this.reverseGeocode(coords.lat, coords.lng)),
      catchError((error) =>
        this.handleError(error, "Erreur lors de la récupération de l'adresse"),
      ),
    );
  }

  // =====================================================
  // UTILITAIRES
  // =====================================================

  /**
   * Nettoie le cache
   */
  clearCache(): void {
    this._locationProfile.set(null);
  }

  /**
   * Mappe un profil utilisateur vers une réponse
   */
  private mapToProfileResponse(
    userId: string,
    profile: UserLocationProfile,
  ): LocationProfileResponse {
    return {
      userId,
      hasLocation: true,
      address: profile.address,
      city: profile.city,
      postalCode: profile.postalCode,
      country: profile.country,
      coordinates: profile.coordinates,
      actionRadius: profile.actionRadius,
      enrichedData: profile.enrichedData,
      enrichedAt: profile.enrichedAt,
      needsUpdate: profile.needsUpdate,
      enrichmentStatus: profile.enrichmentStatus,
    };
  }

  // =====================================================
  // ROUTING (OSRM)
  // =====================================================

  /**
   * Calcule un itinéraire routier entre deux points via le proxy OSRM backend.
   * Retourne un tableau de routes (principale + alternatives).
   */
  getRoute(from: Coordinates, to: Coordinates): Observable<RouteResult[]> {
    const params = {
      fromLat: from.lat.toString(),
      fromLng: from.lng.toString(),
      toLat: to.lat.toString(),
      toLng: to.lng.toString(),
    };
    return this.http
      .get<{ routes: RouteResult[] }>(`${this.apiUrl}/route`, { params })
      .pipe(
        map((res) => res.routes || []),
        catchError((error) =>
          this.handleError(error, "Erreur lors du calcul d'itinéraire"),
        ),
      );
  }

  /**
   * Prévision météo pour une date et des coordonnées (via Open-Meteo, max 16 jours).
   * Retourne { available: false } si hors fenêtre ou erreur.
   */
  getWeatherForDate(
    lat: number,
    lng: number,
    date: string, // YYYY-MM-DD
  ): Observable<EventWeatherForecast> {
    return this.http
      .get<EventWeatherForecast>(`${this.apiUrl}/weather`, {
        params: { lat: lat.toString(), lng: lng.toString(), date },
      })
      .pipe(
        catchError(() =>
          of({ available: false, reason: 'error' } as EventWeatherForecast),
        ),
      );
  }

  /**
   * Prévisions horaires pour un créneau d'événement (aujourd'hui uniquement).
   */
  getWeatherSlots(
    lat: number,
    lng: number,
    date: string,
    startTime?: string,
    endTime?: string,
  ): Observable<WeatherSlotsResponse> {
    const params: Record<string, string> = {
      lat: lat.toString(),
      lng: lng.toString(),
      date,
    };
    if (startTime) params['startTime'] = startTime;
    if (endTime) params['endTime'] = endTime;
    return this.http
      .get<WeatherSlotsResponse>(`${this.apiUrl}/weather-slots`, { params })
      .pipe(
        catchError(() =>
          of({ available: false, reason: 'error' } as WeatherSlotsResponse),
        ),
      );
  }

  /**
   * Événements locaux à venir (7 jours) autour des coordonnées.
   * Utilise OpenAgenda + Eventbrite + Ticketmaster via le backend.
   */
  getLocalEvents(
    lat: number,
    lng: number,
    radiusKm = 15,
  ): Observable<NearbyEventsResponse> {
    return this.http
      .get<NearbyEventsResponse>(`${this.apiUrl}/local-events`, {
        params: {
          lat: lat.toString(),
          lng: lng.toString(),
          radiusKm: radiusKm.toString(),
        },
      })
      .pipe(
        catchError(() =>
          of({ success: false, events: [], total: 0 } as NearbyEventsResponse),
        ),
      );
  }

  /**
   * Gestion centralisée des erreurs HTTP
   */
  private handleError(
    error: HttpErrorResponse,
    userMessage: string,
  ): Observable<never> {
    // Ne pas logger les refus de géolocalisation (choix utilisateur, pas une erreur)
    if (
      !(
        error instanceof Error &&
        error.message?.includes('géolocalisation refusée')
      )
    ) {
      this.log.error('Erreur API Location', { status: (error as any).status });
    }

    let errorMessage = userMessage;

    if (error.error instanceof ErrorEvent) {
      // Erreur côté client
      errorMessage = `${userMessage}: ${error.error.message}`;
    } else {
      // Erreur côté serveur
      if (error.error?.message) {
        errorMessage = `${userMessage}: ${error.error.message}`;
      } else {
        errorMessage = `${userMessage} (Code: ${error.status})`;
      }
    }

    return throwError(() => new Error(errorMessage));
  }

  /**
   * Polling pour vérifier le statut d'un job
   * (non implémenté côté backend dans cette version)
   */
  pollJobStatus(jobId: string, intervalMs: number = 3000): Observable<any> {
    return timer(0, intervalMs).pipe(
      switchMap(() =>
        this.http
          .get(`${this.apiUrl}/jobs/${jobId}`)
          .pipe(catchError(() => of({ status: 'unknown' }))),
      ),
    );
  }
}
